import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar, BookOpen, Monitor, Target, Briefcase, Users, BarChart3, Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Extracted tab components
import { ProgrammeesTab } from '../components/formations/ProgrammeesTab';
import { CatalogueTab } from '../components/formations/CatalogueTab';
import { ElearningTab } from '../components/formations/ElearningTab';
import { BilanTab } from '../components/formations/BilanTab';
import { FormationDialogs } from '../components/formations/FormationDialogs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const FORMATIONS_PORTAL_URL = 'https://formation-portal-1.emergent.host/';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const categoryIcons = {
  'Bureautique': Monitor,
  'Finances / Comptabilité': Calculator,
  'Ressources Humaines': Users,
  'Management & Développement': Briefcase,
  'Développement Personnel': Target,
  'Comptabilité': BarChart3
};

const levels = ['Tous niveaux', 'Débutant', 'Intermédiaire', 'Avancé'];

const ELEARNING_PRICES = {
  'Débutant': 12000,
  'Intermédiaire': 18000,
  'Avancé': 22000
};

export const FormationsPage = () => {
  const navigate = useNavigate();
  const { user, token, login, register } = useAuth();
  const [activeTab, setActiveTab] = useState('programmees');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('Tous niveaux');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [infoRequestOpen, setInfoRequestOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', position: '', financing: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState({});

  // E-Learning sub-views
  const [elearningView, setElearningView] = useState('courses');
  const [selectedElearningCourse, setSelectedElearningCourse] = useState(null);
  const [pricingTab, setPricingTab] = useState('abonnements');
  const [elearningSearch, setElearningSearch] = useState('');
  const [elearningCategory, setElearningCategory] = useState('all');
  const [elearningLevel, setElearningLevel] = useState('Tous niveaux');
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseItem, setPurchaseItem] = useState(null);
  const [purchaseFormData, setPurchaseFormData] = useState({ name: '', email: '', phone: '', company: '' });
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auth modal state
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '', phone: '', company: '' });
  const [authLoading, setAuthLoading] = useState(false);

  // Server data
  const [catalogData, setCatalogData] = useState([]);
  const [serverCourses, setServerCourses] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Fetch catalog + courses
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [catalogRes, coursesRes] = await Promise.all([
          axios.get(`${API}/courses/catalog`),
          axios.get(`${API}/courses`)
        ]);
        setCatalogData(catalogRes.data.map(c => ({ ...c, id: c.catalog_id })));
        setServerCourses(coursesRes.data);
      } catch (e) {
        console.error('Failed to fetch data:', e);
      } finally {
        setCatalogLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Derive e-learning courses
  const allElearningCourses = useMemo(() => {
    const serverMap = {};
    serverCourses.forEach(sc => { serverMap[sc.catalog_id] = sc; });
    return catalogData.map(course => {
      const server = serverMap[course.id];
      const programModules = course.program || [];
      const lessonCount = server ? Math.max((server.slide_count || 6) - 2, 1) : programModules.length || 4;
      return {
        ...course, elearningId: `el-${course.id}`, modules: programModules,
        modulesCount: lessonCount, totalMinutes: lessonCount * 15,
        price: ELEARNING_PRICES[course.level] || 18000, modality: 'E-Learning'
      };
    });
  }, [catalogData, serverCourses]);

  // Show auth modal from URL param
  useEffect(() => {
    if (searchParams.get('showLogin') === 'true') {
      setAuthModalOpen(true); setActiveTab('elearning'); setElearningView('courses'); setSearchParams({});
    }
  }, []);

  // Map item to server-side package_id
  const getPackageId = useCallback((item) => {
    if (!item) return null;
    if (item.type === 'subscription') return `sub-${item.id}`;
    if (item.type === 'pack') return item.id;
    const course = catalogData.find(c => c.id === item.id);
    if (course) {
      const levelMap = { 'Débutant': 'course-debutant', 'Intermédiaire': 'course-intermediaire', 'Avancé': 'course-avance' };
      return levelMap[course.level] || 'course-intermediaire';
    }
    return null;
  }, [catalogData]);

  // Payment status polling
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'cancelled') {
      toast.info('Paiement annulé. Vous pouvez réessayer.'); setSearchParams({}); setActiveTab('elearning'); setElearningView('courses'); return;
    }
    if (sessionId && paymentStatus === 'success') {
      setActiveTab('elearning'); setElearningView('courses'); pollPaymentStatus(sessionId, 0);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 5) { toast.info('Le statut de votre paiement est en cours de traitement.'); setSearchParams({}); return; }
    try {
      const res = await axios.get(`${API}/stripe/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentSuccess(true); setPaymentDetails(res.data.metadata); toast.success('Paiement confirmé !'); setSearchParams({}); return;
      } else if (res.data.status === 'expired') { toast.error('Session de paiement expirée.'); setSearchParams({}); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { toast.error('Erreur lors de la vérification du paiement.'); setSearchParams({}); }
  };

  // Dynamic categories
  const categories = useMemo(() => {
    const catSet = ['Bureautique', 'Finances / Comptabilité', 'Ressources Humaines', 'Management & Développement'];
    catalogData.forEach(c => { if (c.category && !catSet.includes(c.category)) catSet.push(c.category); });
    return [
      { id: 'all', name: 'Toutes catégories', count: catalogData.length },
      ...catSet.map(cat => ({ id: cat, name: cat, count: catalogData.filter(t => t.category === cat).length, icon: categoryIcons[cat] || Briefcase }))
    ];
  }, [catalogData]);

  // Filtered catalog
  const filteredCatalog = useMemo(() => {
    return catalogData.filter(t => {
      return (selectedCategory === 'all' || t.category === selectedCategory) &&
        (selectedLevel === 'Tous niveaux' || t.level === selectedLevel) &&
        (searchQuery === '' || t.title.toLowerCase().includes(searchQuery.toLowerCase()) || t.description.toLowerCase().includes(searchQuery.toLowerCase()));
    });
  }, [selectedCategory, selectedLevel, searchQuery, catalogData]);

  // Filtered e-learning
  const filteredElearning = useMemo(() => {
    return allElearningCourses.filter(c => {
      return (elearningCategory === 'all' || c.category === elearningCategory) &&
        (elearningLevel === 'Tous niveaux' || c.level === elearningLevel) &&
        (elearningSearch === '' || c.title.toLowerCase().includes(elearningSearch.toLowerCase()) || c.description.toLowerCase().includes(elearningSearch.toLowerCase()));
    });
  }, [elearningCategory, elearningLevel, elearningSearch, allElearningCourses]);

  // Handlers
  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!purchaseFormData.name || !purchaseFormData.email || !purchaseFormData.phone) { toast.error('Veuillez remplir tous les champs obligatoires'); return; }
    setSubmitting(true);
    try {
      const packageId = getPackageId(purchaseItem);
      if (!packageId) { toast.error('Erreur: package invalide'); return; }
      const res = await axios.post(`${API}/stripe/checkout`, {
        package_id: packageId, origin_url: window.location.origin,
        customer_name: purchaseFormData.name, customer_email: purchaseFormData.email,
        customer_phone: purchaseFormData.phone, course_title: purchaseItem?.title,
        user_id: user?.id || null, catalog_course_id: purchaseItem?.type === 'course' ? purchaseItem?.id : null
      });
      if (res.data.url) { window.location.href = res.data.url; } else { toast.error('Erreur lors de la création de la session de paiement'); }
    } catch { toast.error("Erreur lors de l'envoi. Veuillez réessayer."); } finally { setSubmitting(false); }
  };

  const openPurchaseDialog = (item) => {
    setPurchaseItem(item);
    if (user) { setPurchaseFormData({ name: user.name || '', email: user.email || '', phone: user.phone || '', company: user.company || '' }); }
    setPurchaseDialogOpen(true);
  };

  const handleAuth = async (e) => {
    e.preventDefault(); setAuthLoading(true);
    try {
      if (authMode === 'login') { await login(authForm.email, authForm.password); toast.success('Connexion réussie !'); }
      else { if (!authForm.name || !authForm.email || !authForm.password) { toast.error('Veuillez remplir tous les champs'); setAuthLoading(false); return; } await register(authForm); toast.success('Compte créé avec succès !'); }
      setAuthModalOpen(false); setAuthForm({ name: '', email: '', password: '', phone: '', company: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur, veuillez réessayer'); } finally { setAuthLoading(false); }
  };

  const handleViewDetail = (training) => { setSelectedTraining(training); setShowDetail(true); };
  const handleBackToCatalog = () => { setShowDetail(false); setSelectedTraining(null); };
  const handleOpenRegistration = (training) => { setSelectedTraining(training); setRegistrationOpen(true); };
  const handleOpenInfoRequest = (training) => { setSelectedTraining(training); setInfoRequestOpen(true); };

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) { toast.error('Veuillez remplir tous les champs obligatoires'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/training-registrations`, {
        training_id: selectedTraining.id, training_title: selectedTraining.title, training_type: type,
        name: formData.name, email: formData.email, phone: formData.phone, company: formData.company,
        position: formData.position, financing: formData.financing, message: formData.message, price: selectedTraining.price || null
      });
      toast.success(type === 'inscription' ? 'Inscription envoyée !' : 'Demande envoyée !');
      setRegistrationOpen(false); setInfoRequestOpen(false);
      setFormData({ name: '', email: '', phone: '', company: '', position: '', financing: '', message: '' });
    } catch { toast.error("Erreur lors de l'envoi. Veuillez réessayer."); } finally { setSubmitting(false); }
  };

  const formatPrice = (price) => new Intl.NumberFormat('fr-FR').format(price) + ' XPF';
  const handleGoToPortal = () => window.open(FORMATIONS_PORTAL_URL, '_blank', 'noopener,noreferrer');

  return (
    <div className="overflow-hidden" data-testid="formations-page">
      {/* Hero Section */}
      <section className="relative py-14 bg-teal-700" data-testid="formations-hero">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="tapa" width="20" height="20" patternUnits="userSpaceOnUse">
              <rect width="20" height="20" fill="none"/>
              <path d="M0 10 L10 0 L20 10 L10 20 Z" fill="currentColor" opacity="0.3"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#tapa)"/>
          </svg>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div initial="initial" animate="animate" variants={fadeInUp}>
            <span className="inline-block bg-gold-500/20 text-gold-400 px-4 py-2 rounded-full text-sm font-medium mb-6">AERH Formations Tahiti</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">Centre de formation professionnelle</h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto mb-8">
              {catalogData.length} modules de formation adaptés aux réalités polynésiennes. Présentiel, e-learning et bilan de compétences.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { id: 'programmees', icon: Calendar, label: 'Programmées' },
                { id: 'catalogue', icon: BookOpen, label: 'Catalogue' },
                { id: 'elearning', icon: Monitor, label: 'E-Learning' },
                { id: 'bilan', icon: Target, label: 'Bilan de compétences & Transition professionnelle' }
              ].map(tab => (
                <Button key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setShowDetail(false); if (tab.id === 'elearning') setElearningView('courses'); }}
                  className={`rounded-full px-6 py-5 ${activeTab === tab.id ? 'bg-gold-500 text-slate-900' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />{tab.label}
                </Button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatePresence mode="wait">
        {activeTab === 'programmees' && <ProgrammeesTab handleGoToPortal={handleGoToPortal} />}
        {activeTab === 'catalogue' && (
          <CatalogueTab
            showDetail={showDetail} selectedTraining={selectedTraining}
            catalogData={catalogData} filteredCatalog={filteredCatalog}
            categories={categories} levels={levels}
            selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
            selectedLevel={selectedLevel} setSelectedLevel={setSelectedLevel}
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            handleViewDetail={handleViewDetail} handleBackToCatalog={handleBackToCatalog}
            handleOpenRegistration={handleOpenRegistration} handleOpenInfoRequest={handleOpenInfoRequest}
          />
        )}
        {activeTab === 'elearning' && (
          <ElearningTab
            user={user} elearningView={elearningView} setElearningView={setElearningView}
            selectedElearningCourse={selectedElearningCourse} setSelectedElearningCourse={setSelectedElearningCourse}
            filteredElearning={filteredElearning} allElearningCourses={allElearningCourses}
            categories={categories} levels={levels}
            elearningSearch={elearningSearch} setElearningSearch={setElearningSearch}
            elearningCategory={elearningCategory} setElearningCategory={setElearningCategory}
            elearningLevel={elearningLevel} setElearningLevel={setElearningLevel}
            pricingTab={pricingTab} setPricingTab={setPricingTab}
            paymentSuccess={paymentSuccess} paymentDetails={paymentDetails} setPaymentSuccess={setPaymentSuccess}
            openPurchaseDialog={openPurchaseDialog} formatPrice={formatPrice}
            setAuthModalOpen={setAuthModalOpen} setAuthMode={setAuthMode}
          />
        )}
        {activeTab === 'bilan' && (
          <BilanTab expandedPhases={expandedPhases} setExpandedPhases={setExpandedPhases} handleOpenRegistration={handleOpenRegistration} />
        )}
      </AnimatePresence>

      <FormationDialogs
        registrationOpen={registrationOpen} setRegistrationOpen={setRegistrationOpen}
        selectedTraining={selectedTraining} formData={formData} setFormData={setFormData}
        handleSubmit={handleSubmit} submitting={submitting}
        infoRequestOpen={infoRequestOpen} setInfoRequestOpen={setInfoRequestOpen}
        purchaseDialogOpen={purchaseDialogOpen} setPurchaseDialogOpen={setPurchaseDialogOpen}
        purchaseItem={purchaseItem} purchaseFormData={purchaseFormData} setPurchaseFormData={setPurchaseFormData}
        handlePurchase={handlePurchase} formatPrice={formatPrice}
        authModalOpen={authModalOpen} setAuthModalOpen={setAuthModalOpen}
        authMode={authMode} setAuthMode={setAuthMode}
        authForm={authForm} setAuthForm={setAuthForm}
        authLoading={authLoading} handleAuth={handleAuth}
      />
    </div>
  );
};

export default FormationsPage;
